import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { AppLayoutComponent, HeaderConfig, SidebarConfig, NavItem } from './layout';
import { FidService } from './core/services/fid.service';
import { MetadataStoreService } from './core/services/metadata-store.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MatButtonModule, AppLayoutComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'parent-portal';
  currentFid: string = '';
  private fidSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private metadataSubscription?: Subscription;

  headerConfig: HeaderConfig = {
    title: 'SyncUI',
    showMenuButton: false // This will be auto-managed by the layout component
  };

  sidebarConfig: SidebarConfig = {
    logo: {
      icon: 'security',
      title: 'Securly'
    },
    navItems: [] // Start with empty array - items will be shown based on principals
  };

  // Define all available nav items with their required principals
  private allNavItems: Array<NavItem & { requiredPrincipals: string[] }> = [
    { 
      label: 'FID Picker', 
      icon: '🔍', 
      route: '/fids', 
      active: false,
      disabled: false,
      requiredPrincipals: ['support'] // Show only to support (customer auto-submits and bypasses)
    },
    { 
      label: 'Data Readiness', 
      icon: '📊', 
      route: '/fids/placeholder/data-readiness', 
      active: false,
      disabled: true,
      requiredPrincipals: ['support', 'customer'] // Show to both support and customer
    },
    { 
      label: 'Data Viewer', 
      icon: '👁️', 
      route: '/fids/placeholder/data-viewer', 
      active: false,
      disabled: true,
      requiredPrincipals: ['support', 'customer'] // Show to both support and customer
    },
    { 
      label: 'User Management', 
      icon: '👥', 
      route: '/users', 
      active: false,
      disabled: false,
      requiredPrincipals: ['support'] // Show only to support (not customer)
    }
  ];

  constructor(
    private router: Router, 
    private fidService: FidService,
    private metadataStore: MetadataStoreService
  ) {}

  ngOnInit() {
    // Subscribe to FID changes
    this.fidSubscription = this.fidService.currentFid$.subscribe(fid => {
      if (fid) {
        this.updateFid(fid);
      } else {
        // FID was cleared, disable other menu items
        this.disableOtherMenuItems();
      }
    });

    // Subscribe to route changes to update active state
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateActiveState((event as NavigationEnd).url);
      });

    // Subscribe to metadata changes to filter nav items
    this.metadataSubscription = this.metadataStore.metadata$.subscribe(metadata => {
      this.updateNavItemsBasedOnMetadata(metadata);
    });

    // Check initial metadata state
    const initialMetadata = this.metadataStore.getMetadata();
    if (initialMetadata) {
      this.updateNavItemsBasedOnMetadata(initialMetadata);
    }

    // Set initial active state
    this.updateActiveState(this.router.url);
  }

  ngOnDestroy() {
    if (this.fidSubscription) {
      this.fidSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.metadataSubscription) {
      this.metadataSubscription.unsubscribe();
    }
  }

  onNavItemClick(item: NavItem) {
    console.log('Navigation clicked:', item);
    
    // Handle navigation for all items
    if (item.label === 'FID Picker') {
      if (this.currentFid) {
        // Navigate to FID-specific FID Picker route
        this.router.navigate(['/fids', this.currentFid]);
      } else {
        // Navigate to general FID Picker route
        this.router.navigate(['/fids']);
      }
    } else if (item.label === 'Data Readiness' && this.currentFid) {
      this.router.navigate(['/fids', this.currentFid, 'data-readiness']);
    } else if (item.label === 'Data Viewer' && this.currentFid) {
      this.router.navigate(['/fids', this.currentFid, 'data-viewer']);
    } else if (item.label === 'User Management') {
      this.router.navigate(['/users']);
    }
  }

  // Method to update the current FID and enable navigation
  updateFid(fid: string) {
    this.currentFid = fid;
    this.sidebarConfig.navItems.forEach(navItem => {
      if (navItem.label === 'FID Picker') {
        // Update FID Picker route to include the FID
        navItem.route = `/fids/${fid}`;
      } else {
        navItem.disabled = false;
        // Update routes with actual FID
        if (navItem.label === 'Data Readiness') {
          navItem.route = `/fids/${fid}/data-readiness`;
        } else if (navItem.label === 'Data Viewer') {
          navItem.route = `/fids/${fid}/data-viewer`;
        }
      }
    });
  }

  // Method to disable other menu items when FID is cleared
  disableOtherMenuItems() {
    this.currentFid = '';
    this.sidebarConfig.navItems.forEach(navItem => {
      if (navItem.label === 'FID Picker') {
        // Reset FID Picker route to general route
        navItem.route = '/fids';
      } else if (navItem.label === 'User Management') {
        // User Management doesn't require a FID, so keep it enabled
        navItem.disabled = false;
        navItem.route = '/users';
      } else {
        // Disable other menu items
        navItem.disabled = true;
        navItem.route = navItem.label === 'Data Readiness' 
          ? '/fids/placeholder/data-readiness' 
          : '/fids/placeholder/data-viewer';
      }
    });
  }

  // Method to update active state based on current URL
  updateActiveState(url: string) {
    // Reset all active states
    this.sidebarConfig.navItems.forEach(navItem => {
      navItem.active = false;
    });

    // Determine which item should be active based on URL
    if (url === '/users' || url.startsWith('/users/')) {
      // User Management route
      const userManagementItem = this.sidebarConfig.navItems.find(item => item.label === 'User Management');
      if (userManagementItem) {
        userManagementItem.active = true;
      }
    } else if (url === '/fids' || url.startsWith('/fids/') && !url.includes('/data-readiness') && !url.includes('/data-viewer')) {
      // FID Picker route (either /fids or /fids/{fid})
      const fidPickerItem = this.sidebarConfig.navItems.find(item => item.label === 'FID Picker');
      if (fidPickerItem) {
        fidPickerItem.active = true;
      }
    } else if (url.includes('/data-readiness')) {
      // Data Readiness route
      const dataReadinessItem = this.sidebarConfig.navItems.find(item => item.label === 'Data Readiness');
      if (dataReadinessItem) {
        dataReadinessItem.active = true;
      }
    } else if (url.includes('/data-viewer')) {
      // Data Viewer route
      const dataViewerItem = this.sidebarConfig.navItems.find(item => item.label === 'Data Viewer');
      if (dataViewerItem) {
        dataViewerItem.active = true;
      }
    }
  }

  onLayoutChange(layout: { isMobile: boolean; sidebarOpen: boolean }) {
    console.log('Layout changed:', layout);
    // Handle layout changes if needed
  }

  /**
   * Updates nav items based on metadata, showing only items that match user's principals
   * If metadata is not available, all items are hidden
   */
  private updateNavItemsBasedOnMetadata(metadata: any): void {
    // If no metadata, hide all items
    if (!metadata) {
      this.sidebarConfig.navItems = [];
      return;
    }

    // Get user principals from metadata
    const userPrincipals = metadata?.user?.principals || [];
    const principalsArray = Array.isArray(userPrincipals) ? userPrincipals : [];

    // If no principals, hide all items
    if (principalsArray.length === 0) {
      this.sidebarConfig.navItems = [];
      return;
    }

    // Filter nav items based on required principals
    // An item is shown if the user has at least one of the required principals
    const visibleItems = this.allNavItems
      .filter(item => {
        // Check if user has at least one of the required principals
        return item.requiredPrincipals.some(requiredPrincipal => 
          principalsArray.includes(requiredPrincipal)
        );
      })
      .map(item => {
        // Create a copy without the requiredPrincipals property
        const { requiredPrincipals, ...navItem } = item;
        return navItem;
      });

    // Preserve current FID state before applying updates
    const currentFid = this.currentFid;

    // Apply the filtered items
    this.sidebarConfig.navItems = visibleItems;

    // Reapply FID state and active states
    if (currentFid) {
      this.updateFid(currentFid);
    } else {
      this.disableOtherMenuItems();
    }
    this.updateActiveState(this.router.url);
  }
}
